import { timestampDate } from '@bufbuild/protobuf/wkt';
import { TenantStatus } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import {
  useCheckTenantDeleteBlockers,
  useDeleteTenant,
  useGetHealthTimeline,
  useGetTenant,
  useInfiniteListAuditEvents,
  useListDeployments,
  useListTenants,
  useListUsers,
} from '@sunstone/api-client/hooks';
import { Card, CardContent, Tabs, TabsContent, TabsList, TabsTrigger } from '@sunstone/ui';
import { formatDateTime } from '@sunstone/utils';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AuditHistory } from '@/components/ui/AuditHistory';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { DeploymentList } from '@/features/deployments/DeploymentList';
import { type Blip, InlineHealthStrip } from '@/features/health/HealthBlips';
import { TenantList } from '@/features/tenants/TenantList';
import { MemberList } from '@/features/users/MemberList';
import { RoleTable } from '@/features/users/RoleTable';
import { flags } from '@/lib/flags';
import { routes } from '@/lib/routes';

// Map proto enum numbers to the string keys StatusBadge expects
function tenantStatusKey(status: TenantStatus): string {
  switch (status) {
    case TenantStatus.ACTIVE:
      return 'TENANT_STATUS_ACTIVE';
    case TenantStatus.INACTIVE:
      return 'TENANT_STATUS_INACTIVE';
    default:
      return 'TENANT_STATUS_UNSPECIFIED';
  }
}

interface TenantDetailProps {
  tenantId: string;
  deleteOpen?: boolean;
  onDeleteOpenChange?: (open: boolean) => void;
}

export function TenantDetail({
  tenantId,
  deleteOpen: propDeleteOpen,
  onDeleteOpenChange,
}: TenantDetailProps) {
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { isPlatformAdmin, canManageAccess } = useAuthorization();

  const {
    data: tenantData,
    isLoading: tenantLoading,
    error: tenantError,
  } = useGetTenant(tenantId, token);
  const tenant = tenantData?.tenant;

  const isPartner = tenant ? !tenant.parentId : false;

  const toHour = new Date();
  toHour.setMinutes(0, 0, 0);
  const { data: healthData, isLoading: healthLoading } = useGetHealthTimeline(
    token,
    {
      id: tenant?.id ?? '',
      level: isPartner ? 'sunstone_partner' : 'sunstone_tenant',
      from: new Date(toHour.getTime() - 24 * 60 * 60 * 1000).toISOString(),
      to: toHour.toISOString(),
      grain: 'hour',
      overallOnly: false,
    },
    !!tenant,
  );
  const overallBlips: Blip[] = healthData?.overall ?? [];
  const healthSubtotals = healthData?.subtotals ?? [];
  const tenantBlipsByEntityId: Record<string, Blip[]> = Object.fromEntries(
    healthSubtotals.filter((s) => s.level === 'sunstone_tenant').map((s) => [s.id, s.blips]),
  );
  const deploymentBlipsByEntityId: Record<string, Blip[]> = Object.fromEntries(
    healthSubtotals.filter((s) => s.level === 'sunstone_deployment').map((s) => [s.id, s.blips]),
  );

  // Fetch parent name for a tenant-less (platform) Admin only — other roles don't see the Partner field
  const { data: parentData } = useGetTenant(isPlatformAdmin ? (tenant?.parentId ?? '') : '', token);
  const parent = parentData?.tenant ?? null;

  // pageSize:1 count queries — totalCount is unaffected by the list's own search/filter state
  const { data: childTenantCountData } = useListTenants(
    token,
    tenantId,
    { pageSize: 1, typeFilter: 'customer' },
    isPartner,
  );
  const { data: deploymentCountData } = useListDeployments(
    token,
    isPartner ? undefined : tenantId,
    isPartner ? tenantId : undefined,
    { pageSize: 1 },
    !!tenant,
  );
  const { data: userCountData } = useListUsers(canManageAccess ? tenantId : '', token, {
    pageSize: 1,
  });

  const childTenantCount = childTenantCountData?.totalCount ?? 0;
  const deploymentCount = deploymentCountData?.totalCount ?? 0;
  const userCount = userCountData?.totalCount ?? 0;

  const {
    data: auditData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteListAuditEvents(token, 'tenant', tenantId);
  const auditEvents = auditData?.pages.flatMap((p) => p.events) ?? [];

  const defaultTab = isPartner ? 'customers' : 'deployments';

  const [internalDeleteOpen, setInternalDeleteOpen] = useState(false);
  const deleteOpen = propDeleteOpen ?? internalDeleteOpen;
  const setDeleteOpen = onDeleteOpenChange ?? setInternalDeleteOpen;

  const [isDeleting, setIsDeleting] = useState(false);
  const { data: blockersData } = useCheckTenantDeleteBlockers(tenantId, token, deleteOpen);
  const blockingResources = (blockersData?.blockingResources ?? []).map((r) => ({
    id: r.id,
    displayName: r.displayName,
    resourceType: r.resourceType,
  }));
  const deleteTenant = useDeleteTenant(token);

  function handleDelete(mode: 'full' | 'force_tombstone') {
    void mode; // tenants don't use force_tombstone mode
    setIsDeleting(true);
    deleteTenant.mutate(
      { id: tenantId },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          navigate(isPartner ? routes.partners() : routes.customers());
        },
        onSettled: () => setIsDeleting(false),
      },
    );
  }

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">Loading...</div>
    );
  }

  if (tenantError || !tenant) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">Not found.</div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Tenant meta strip */}
      <Card>
        <CardContent className="py-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">URL Name</p>
              <p className="font-mono text-sm text-foreground">{tenant.slug}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Type</p>
              <p className="text-sm text-foreground">{isPartner ? 'Partner' : 'Customer'}</p>
            </div>
            {parent && isPlatformAdmin && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium">Partner</p>
                <p className="text-sm text-foreground">{parent.displayName}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Status</p>
              <StatusBadge status={tenantStatusKey(tenant.status)} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Cost Centre</p>
              <p className="text-sm text-foreground">{tenant.costCentre}</p>
            </div>
            {flags.enableFuture && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium">Org ID</p>
                <p className="font-mono text-sm text-foreground text-ellipsis overflow-hidden">
                  {tenant.organizationId || '—'}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Created</p>
              <p className="text-sm text-foreground">
                {tenant.createdAt ? formatDateTime(timestampDate(tenant.createdAt)) : '—'}
              </p>
            </div>
            <InlineHealthStrip blips={overallBlips} isLoading={healthLoading} />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs key={tenantId} defaultValue={defaultTab} className="space-y-4">
        <TabsList>
          {isPartner && (
            <TabsTrigger value="customers">
              Customers
              <span className="ml-1.5 text-xs bg-muted-foreground/20 rounded-full px-1.5 py-0.5">
                {childTenantCount}
              </span>
            </TabsTrigger>
          )}
          <TabsTrigger value="deployments">
            Deployments
            <span className="ml-1.5 text-xs bg-muted-foreground/20 rounded-full px-1.5 py-0.5">
              {deploymentCount}
            </span>
          </TabsTrigger>
          {canManageAccess && (
            <TabsTrigger value="users">
              Users
              <span className="ml-1.5 text-xs bg-muted-foreground/20 rounded-full px-1.5 py-0.5">
                {userCount}
              </span>
            </TabsTrigger>
          )}
          {canManageAccess && <TabsTrigger value="roles">Roles</TabsTrigger>}
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {isPartner && (
          <TabsContent value="customers">
            <TenantList
              mode="customers"
              parentId={tenantId}
              healthBlipsByEntityId={tenantBlipsByEntityId}
              healthLoading={healthLoading}
            />
          </TabsContent>
        )}

        <TabsContent value="deployments">
          {isPartner ? (
            <DeploymentList
              parentTenantId={tenantId}
              healthBlipsByEntityId={deploymentBlipsByEntityId}
              healthLoading={healthLoading}
            />
          ) : (
            <DeploymentList
              tenantId={tenantId}
              healthBlipsByEntityId={deploymentBlipsByEntityId}
              healthLoading={healthLoading}
            />
          )}
        </TabsContent>

        {canManageAccess && (
          <TabsContent value="users" className="space-y-4">
            <MemberList
              scope="tenant"
              tenantId={tenantId}
              onRowClick={(user) => navigate(routes.userDetail(tenantId, user.id))}
            />
          </TabsContent>
        )}

        {canManageAccess && (
          <TabsContent value="roles" className="space-y-4">
            <RoleTable tenantId={tenantId} />
          </TabsContent>
        )}

        <TabsContent value="history">
          <AuditHistory
            events={auditEvents}
            onLoadMore={fetchNextPage}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
          />
        </TabsContent>
      </Tabs>

      <DeleteConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        resourceName={tenant.displayName}
        resourceType={isPartner ? 'partner' : 'customer'}
        blockingResources={blockingResources}
        supportsForceTombstone={false}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}
